/**
 * Mock data for Creatives Studio demo mode
 * Activated when VITE_ENABLE_MOCK_GENERATION=true or ?mockGeneration=true
 * Provides realistic business data, photos, reviews, and campaign options
 * so the full studio flow can be demonstrated without a backend API.
 *
 * Business: Hearth — a wood-fired restaurant concept.
 * Assets live in .mock/ (gitignored) and are served via the Vite middleware
 * or intercepted by Playwright during screencasts.
 */

// ── Mock Business Data ──────────────────────────────────────────────────────

export const MOCK_BUSINESS = {
  name: 'Hearth — Wood-Fired Kitchen & Bar',
  address: '1535 Platte St, Denver, CO 80202',
  phone: '(720) 555-0142',
  website: 'https://hearthdenver.com',
  category: 'American Restaurant',
  placeId: 'mock-place-hearth-001',
  rating: 4.7,
  reviews_count: 612,
  price_level: 3,
  opening_hours: ['Mon-Thu 5pm-10pm', 'Fri-Sat 5pm-11pm', 'Sun 10am-3pm (Brunch)'],
  logo_data_url: '/.mock/logo.png',
};

// ── Mock Characters ─────────────────────────────────────────────────────────

export const MOCK_CHARACTERS = [];

// ── Mock Photos (served from .mock/ directory) ──────────────────────────────

export const MOCK_PHOTOS = [
  {
    photo_id: 'mock-photo-001',
    photo_name_id: 'hearth-1',
    data_url: '/.mock/hearth_1.png',
    caption: 'Wood-fired oven',
    selected: true,
  },
  {
    photo_id: 'mock-photo-002',
    photo_name_id: 'hearth-2',
    data_url: '/.mock/hearth_2.png',
    caption: 'Dining atmosphere',
    selected: true,
  },
  {
    photo_id: 'mock-photo-003',
    photo_name_id: 'hearth-3',
    data_url: '/.mock/hearth_3.png',
    caption: 'Signature dish',
    selected: true,
  },
  {
    photo_id: 'mock-photo-004',
    photo_name_id: 'hearth-4',
    data_url: '/.mock/hearth_4.png',
    caption: 'Craft cocktails',
    selected: true,
  },
  {
    photo_id: 'mock-photo-005',
    photo_name_id: 'hearth-5',
    data_url: '/.mock/hearth_5.png',
    caption: 'Chef at work',
    selected: false,
  },
];

// ── Mock Reviews ────────────────────────────────────────────────────────────

export const MOCK_REVIEWS = [
  {
    review_id: 'mock-review-001',
    text: 'The wood-fired flavors are incredible. Every dish has that perfect char and smokiness. The seasonal tasting menu was a revelation.',
    rating: 5,
    author: 'Sarah M.',
    selected: true,
  },
  {
    review_id: 'mock-review-002',
    text: 'Absolutely stunning atmosphere. The open kitchen concept lets you watch the chefs work the wood oven. Great wine list too.',
    rating: 5,
    author: 'Marcus T.',
    selected: true,
  },
  {
    review_id: 'mock-review-003',
    text: 'Perfect date night spot. The warm lighting, crackling fire, and attentive service create an unforgettable experience. The charcuterie board is a must.',
    rating: 5,
    author: 'Jennifer L.',
    selected: true,
  },
  {
    review_id: 'mock-review-004',
    text: 'Sunday brunch is the best in Denver. The wood-fired eggs and smoked salmon benedict are worth the wait.',
    rating: 4,
    author: 'David K.',
    selected: false,
  },
  {
    review_id: 'mock-review-005',
    text: 'The 36-hour short rib was melt-in-your-mouth perfection. Paired with their house Cab and it was a truly special evening.',
    rating: 5,
    author: 'Rachel P.',
    selected: false,
  },
];

// ── Mock Campaign Options (mirrors API shape from /config/campaign-options) ─

export const MOCK_CAMPAIGN_OPTIONS = {
  purpose: [
    { value: 'awareness', label: 'Brand Awareness' },
    { value: 'seasonal', label: 'Seasonal Campaign' },
    { value: 'promotion', label: 'Promotion / Offer' },
    { value: 'launch', label: 'New Launch' },
    { value: 'event', label: 'Event' },
    { value: 'retention', label: 'Customer Retention' },
    { value: 'other', label: 'Other' },
  ],
  seasonal_theme: [
    { value: 'new_year', label: 'New Year' },
    { value: 'valentine', label: "Valentine's Day" },
    { value: 'spring', label: 'Spring' },
    { value: 'summer', label: 'Summer' },
    { value: 'back_to_school', label: 'Back to School' },
    { value: 'halloween', label: 'Halloween' },
    { value: 'thanksgiving', label: 'Thanksgiving' },
    { value: 'holiday', label: 'Holiday Season' },
    { value: 'none', label: 'None' },
  ],
  tone: [
    { value: 'auto', label: 'Auto (AI picks best)' },
    { value: 'friendly', label: 'Friendly & Warm' },
    { value: 'professional', label: 'Professional' },
    { value: 'playful', label: 'Playful & Fun' },
    { value: 'elegant', label: 'Elegant' },
    { value: 'bold', label: 'Bold & Energetic' },
    { value: 'humorous', label: 'Humorous' },
    { value: 'luxe', label: 'Luxurious' },
    { value: 'minimalist', label: 'Minimalist' },
    { value: 'rugged', label: 'Rugged' },
    { value: 'modern', label: 'Modern' },
  ],
  channel: [
    { value: 'auto', label: 'Auto (AI picks best)' },
    { value: 'social', label: 'Social Media' },
    { value: 'ctv', label: 'Connected TV (CTV)' },
    { value: 'dooh', label: 'Digital Out-of-Home' },
    { value: 'youtube', label: 'YouTube' },
  ],
};

// ── Mock Campaign Brief ─────────────────────────────────────────────────────

export const MOCK_CAMPAIGN_BRIEF = {
  purpose: 'awareness',
  seasonal_theme: null,
  tone: 'auto',
  channel: 'ctv',
  offers: [],
  notes: '',
};

// ── Mock Video Config ───────────────────────────────────────────────────────

export const MOCK_VIDEO_CONFIG = {
  aspect_ratio: '16:9',
  length: 30,
  resolution: '1920x1080',
  logo_overlay_enabled: true,
  logo_overlay_position: 'bottom-right',
  logo_overlay_opacity: 0.85,
};

// ── Sample Video URL for mock generation completion ─────────────────────────

export const MOCK_JOB_ID = 'e51abc9d-068b-42c4-b4b6-c7238ed1926e';

export const MOCK_VIDEO_URL = '/.mock/e51abc9d-068b-42c4-b4b6-c7238ed1926e.mp4';
